import { Encounter, Patient, Provenance } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounce, useFhirSearch, useMiddlewareClient } from '@/hooks'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import type { AvailResourceType, AvailResource } from '@/hooks/fhirSearchConfig.ts'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ActionIcon, Input, Loader, Menu } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { mergeProvenanceAgents } from '@/utils/provenance'
import {
  IconDotsVertical,
  IconHistory,
  IconRestore,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { usePatientSidePanel } from './PatientSidePanelContext.tsx'
import { CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'
import { buildPatientChartRelativePath, parsePatientChartSplat } from '../patientChartPath'
import { useClient } from '@/hooks/useClient'
import {
  CHART_RECORD_RESOURCE_TYPES,
  IMPORTED_TAG_CODE,
  IMPORTED_TAG_SYSTEM,
  SELF_MANAGED_DOCUMENT_CODES,
  type ChartRecordResourceType,
} from '@/model/middleware-types'
import {
  TypedDeleteConfirmModal,
  type ChartRecordRemovalReason,
} from '@/components/ui/TypedDeleteConfirmModal'
import { ChartRecordActivityModal } from '@/components/ui/ChartRecordActivityModal'
import { CombinedPdfSelectionBar, RecordSelectCheckbox } from './CombinedPdfSelectionBar'
import { isCombinableRecord } from './combinedPdfSelection'
import {
  NOTE_OPEN_IN_EDITOR,
  NoteEditorContext,
  progressNoteRemovalBlock,
  progressNoteVisit,
} from './progressNoteRemoval'

// PD-1685 — the sections whose records can be ticked and downloaded together as one PDF.
const COMBINED_PDF_SECTIONS: Record<
  string,
  { resourceType: 'DiagnosticReport' | 'DocumentReference'; fileLabel: string } | undefined
> = {
  'labs-imaging': { resourceType: 'DiagnosticReport', fileLabel: 'lab results' },
  documents: { resourceType: 'DocumentReference', fileLabel: 'documents' },
}

export const REMOVABLE_DATA_KEYS = new Set([
  'documents',
  'history-physical',
  'labs-imaging',
  'orders',
  'progress-notes',
])

function isImportedResource(resource: {
  meta?: { tag?: Array<{ system?: string; code?: string }> }
}) {
  return (resource.meta?.tag ?? []).some(
    (tag) => tag.system === IMPORTED_TAG_SYSTEM && tag.code === IMPORTED_TAG_CODE,
  )
}

function isChartRecordType(resource: {
  resourceType: string
}): resource is { resourceType: ChartRecordResourceType } & typeof resource {
  return (CHART_RECORD_RESOURCE_TYPES as readonly string[]).includes(resource.resourceType)
}

function isSelfManagedDocument(resource: AvailResource) {
  if (resource.resourceType !== 'DocumentReference') return false
  return (resource.type?.coding ?? []).some(
    (coding) => !!coding.code && SELF_MANAGED_DOCUMENT_CODES.includes(coding.code),
  )
}

// A lab result names its owners in `resultsInterpreter`; a progress note belongs to its visit's
// organization (PD-2031), never to whoever saved it last. Other records are not owner-scoped.
function recordOwners(
  resource: AvailResource,
  visitOwners: Map<string, string>,
): (string | undefined)[] | undefined {
  if (resource.resourceType === 'DiagnosticReport') {
    return (resource.resultsInterpreter ?? []).map((interpreter) => interpreter.reference)
  }
  const visit = progressNoteVisit(resource)
  return visit ? [visitOwners.get(visit)] : undefined
}

function isOtherOrganizationRecord(
  resource: AvailResource,
  organizationRefs: string[] | undefined,
  visitOwners: Map<string, string>,
) {
  const owners = recordOwners(resource, visitOwners)
  if (!owners) return false
  if (!organizationRefs?.length) return true
  return !owners.some((owner) => !!owner && organizationRefs.includes(owner))
}

type Props = {
  patient: Patient
  item: ChartItem
  children: React.ReactNode
  action?: React.ReactNode
}

export const ListLayout = ({ patient, item, children, action }: Props) => {
  const [hasMore, setHasMore] = useState(false)
  const [resources, setResources] = useState<AvailResource[]>([])
  const [provenances, setProvenances] = useState<Provenance[]>([])
  // Encounter reference → its `serviceProvider`, from the `_include`d visits of progress notes.
  const [visitOwners, setVisitOwners] = useState<Map<string, string>>(() => new Map())
  const [searchQuery, setSearchQuery] = useState('')
  const [canClear, setCanClear] = useState(false)
  const [offset, setOffset] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [removingResource, setRemovingResource] = useState<AvailResource | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [noteInEditor, setNoteInEditor] = useState<string | undefined>()
  // Remounts the open record once it is removed or restored, so it shows the status it now has.
  const [openRecordVersion, setOpenRecordVersion] = useState(0)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [activityResource, setActivityResource] = useState<AvailResource | null>(null)
  // PD-1685 — the records ticked for the combined PDF.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const navigate = useNavigate()

  const params = useParams()
  const medplum = useMedplum()
  const client = useClient()
  const queryClient = useQueryClient()
  const middleware = useMiddlewareClient()
  const { panelEventVersions, refreshTarget, clearRefreshTarget } = usePatientSidePanel()
  // Bypass ScrollContext here: CallPage intentionally publishes
  // `stickyTop="0px"` for its internal scroll container, which is wrong for
  // this viewport-`fixed` rail that must clear the Shell header +
  // PatientChartActionBar. Anchor directly to the shared CSS expression.
  const stickyTop = CHART_CONTENT_TOP_VAR
  const progressNotesRefreshVersion = panelEventVersions['progressNotes:refresh'] || 0
  const consumedRefreshVersion = useRef(progressNotesRefreshVersion)
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { search, getSearchQuery, loadMore, setFilter, removeFilter, dropOffset } = useFhirSearch(
    item.resourceTypes[0] as AvailResourceType,
    50,
    item.sort,
  )
  const isLabsImaging = item.resourceTypes[0] === 'DiagnosticReport'
  const combinedPdf = COMBINED_PDF_SECTIONS[item.dataKey]
  const combinableIds = useMemo(
    () => (combinedPdf ? resources.filter(isCombinableRecord).flatMap((r) => r.id ?? []) : []),
    [combinedPdf, resources],
  )
  // The selection only ever holds rows on screen — another section, a record removed from the
  // chart or hidden by the search drops out — so the count matches the ticks shown. (The
  // sections that select are keyed on the patient, so another patient starts fresh.)
  useEffect(() => {
    setSelectedIds((current) => {
      const kept = new Set([...current].filter((id) => combinableIds.includes(id)))
      return kept.size === current.size ? current : kept
    })
  }, [combinableIds])
  const isSuperAdmin = medplum.isSuperAdmin()
  const canSeeRemoved = REMOVABLE_DATA_KEYS.has(item.dataKey) && isSuperAdmin
  const profile = medplum.getProfile()
  // Every organization the practitioner belongs to, not just the first role: the removal grant
  // applies to each of them, so a member of two clinics may remove either clinic's reports.
  const { data: organizationRefs } = useQuery({
    queryKey: ['practitioner-organization-refs', profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const roles = await medplum.searchResources(
        'PractitionerRole',
        `practitioner=Practitioner/${profile?.id}`,
      )
      return roles.flatMap((role) =>
        role.organization?.reference ? [role.organization.reference] : [],
      )
    },
  })

  const openResource = useCallback(
    (resourceId: string) => {
      void navigate(buildPatientChartRelativePath(item.dataKey, resourceId))
      clearRefreshTarget()
    },
    [item.dataKey, navigate, clearRefreshTarget],
  )

  const autoOpenNewResource = useCallback(
    async (resourceList: AvailResource[]) => {
      if (!refreshTarget) {
        return
      }
      if (refreshTarget.kind === 'newestInTab') {
        const target = refreshTarget.dataKey === item.dataKey ? resourceList[0] : undefined
        if (target?.id) {
          openResource(target.id)
        }
        return
      }

      const found = resourceList.find(
        (r) => `${r.resourceType}/${r.id}` === refreshTarget.reference,
      )
      if (found?.id) {
        openResource(found.id)
        return
      }

      const [refResourceType, refId] = refreshTarget.reference.split('/')
      if (!refId || !item.resourceTypes.includes(refResourceType)) {
        return
      }

      if (isLabsImaging) {
        openResource(refId)
        return
      }

      try {
        const result = await medplum.search(
          refResourceType as AvailResourceType,
          `_id=${refId}&${item.relation}=Patient/${patient.id}${item.extraQuery ? `&${item.extraQuery}` : ''}`,
        )
        if (result.entry?.length) {
          openResource(refId)
        }
      } catch (e) {
        logError('patient', 'Failed to verify refresh target ownership', e)
      }
    },
    [refreshTarget, item, isLabsImaging, medplum, patient.id, openResource],
  )

  useEffect(() => {
    if (isLabsImaging) {
      if (debouncedSearchQuery && !canClear) {
        setCanClear(true)
      } else if (!debouncedSearchQuery && canClear) {
        setCanClear(false)
      }
      setOffset(0)
      return
    }

    if (debouncedSearchQuery) {
      item.searchFields.map((field) => setFilter(field, 'contains', debouncedSearchQuery))
      if (!canClear) {
        setCanClear(true)
      }
    } else {
      if (canClear) {
        item.searchFields.map((field) => removeFilter(field))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery])

  useEffect(() => {
    const isRefreshRun = progressNotesRefreshVersion !== consumedRefreshVersion.current

    if (isLabsImaging) {
      if (isRefreshRun && offset !== 0) {
        setOffset(0)
        return
      }
      consumedRefreshVersion.current = progressNotesRefreshVersion
      void loadPatientResources()
      return
    }

    if (isRefreshRun && (search.offset || 0) !== 0) {
      dropOffset()
      return
    }
    consumedRefreshVersion.current = progressNotesRefreshVersion

    const searchQuery = getSearchQuery()
    if (medplum && searchQuery) {
      const resourceType = item.resourceTypes[0] as AvailResourceType
      const removedFilter =
        REMOVABLE_DATA_KEYS.has(item.dataKey) && !canSeeRemoved
          ? '&status:not=entered-in-error'
          : ''
      const searchParams = `${item.relation}=Patient/${patient.id}${item.extraQuery ? `&${item.extraQuery}` : ''}${removedFilter}${item.include ? `&_include=${resourceType}:${item.include}` : ''}&_revinclude=Provenance:target`
      setIsLoading(true)
      medplum
        .search(item.resourceTypes[0] as AvailResourceType, `${searchQuery}&${searchParams}`)
        .then((result) => {
          let resourceList: AvailResource[] = []
          const provenanceList: Provenance[] = []
          const owners = new Map<string, string>()
          for (const entry of result.entry || []) {
            const resource = entry.resource as AvailResource | Encounter
            if (!resource) {
              continue
            }

            if (resource.resourceType === 'Provenance') {
              provenanceList.push(resource)
              continue
            }

            if (resource.resourceType === 'Encounter') {
              const owner = resource.serviceProvider?.reference
              if (owner) owners.set(`Encounter/${resource.id}`, owner)
              continue
            }

            resourceList.push(resource)
          }

          if (search.offset !== 0) {
            const ids = resourceList.map((item) => item.id)
            resourceList = [...resources.filter((item) => !ids.includes(item.id)), ...resourceList]
          }
          if (item.sortItems) {
            resourceList = item.sortItems(resourceList) as unknown as AvailResource[]
          }

          setProvenances((prev) => {
            const newProvenances = provenanceList.filter((p) => !prev.find((pp) => pp.id === p.id))
            return [...prev, ...newProvenances]
          })
          setVisitOwners((prev) => new Map([...prev, ...owners]))
          setResources(resourceList)
          void autoOpenNewResource(resourceList)
          setHasMore(!!result.link?.find((item) => item.relation === 'next'))
        })
        .catch((e) => {
          logError('patient', 'Failed to fetch resources', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch resources',
            color: 'red',
          })
        })
        .finally(() => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, offset, medplum, progressNotesRefreshVersion, search])

  const loadPatientResources = async () => {
    setIsLoading(true)
    try {
      const count = 50
      const sort = item.sort
      const result = await middleware.get('/patient/{id}/labs-imaging', {
        id: patient.id || '',
        count,
        offset,
        sort,
        ...(debouncedSearchQuery ? { searchText: debouncedSearchQuery } : {}),
        ...(canSeeRemoved ? { includeRemoved: true } : {}),
      })

      let resourceList: AvailResource[] = []
      const provenanceList: Provenance[] = []
      for (const resource of result.resources) {
        if (resource.resourceType === 'Provenance') {
          provenanceList.push(resource as Provenance)
          continue
        }

        resourceList.push(resource as AvailResource)
      }
      if (offset !== 0) {
        resourceList = [...resources, ...resourceList]
      }

      const sortedList = item.sortItems
        ? (item.sortItems(resourceList) as unknown as AvailResource[])
        : resourceList
      setResources(sortedList)
      setProvenances(provenanceList)
      setHasMore(!!result.next)
      void autoOpenNewResource(sortedList)
    } catch (e) {
      logError('patient', 'Failed to fetch labs/imaging resources', e)
      showNotification({
        title: 'Error',
        message: 'Failed to fetch resources',
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoadMore = () => {
    if (isLabsImaging) {
      setOffset((prev) => prev + 50)
      return
    }
    loadMore()
  }

  const isRemovedResource = (resource: AvailResource) =>
    isChartRecordType(resource) && resource.status === 'entered-in-error'

  const isProgressNotes = item.dataKey === 'progress-notes'

  // Allowed to remove this kind of record, whether or not this one qualifies.
  const mayRemoveFromChart = (resource: AvailResource) =>
    REMOVABLE_DATA_KEYS.has(item.dataKey) &&
    isChartRecordType(resource) &&
    resource.status !== 'entered-in-error' &&
    !isSelfManagedDocument(resource) &&
    (isSuperAdmin || !isOtherOrganizationRecord(resource, organizationRefs, visitOwners))

  // Progress notes keep the action visible but disabled, with the reason, on the notes it skips.
  const removalBlock = (resource: AvailResource) => {
    if (!isProgressNotes || !mayRemoveFromChart(resource)) return undefined
    return (
      progressNoteRemovalBlock(resource) ??
      (resource.id === noteInEditor ? NOTE_OPEN_IN_EDITOR : undefined)
    )
  }

  const canRemoveFromChart = (resource: AvailResource) =>
    mayRemoveFromChart(resource) && !isImportedResource(resource) && !removalBlock(resource)

  const canRestoreFromChart = (resource: AvailResource) =>
    canSeeRemoved && isRemovedResource(resource)

  const handleRestore = async (resource: AvailResource) => {
    if (!resource.id) {
      return
    }
    const resourceType = resource.resourceType as ChartRecordResourceType
    const resourceId = resource.id
    setRestoringId(resourceId)
    try {
      const result = await client.chartRecordRestore({ resourceType, resourceId })
      setResources((prev) =>
        prev.map((item) =>
          item.id === resourceId ? ({ ...item, status: result.status } as AvailResource) : item,
        ),
      )

      try {
        medplum.invalidateSearches(resourceType)
        if (patient.id) {
          await queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patient.id) })
        }
      } catch (error) {
        logError('patient', 'Refresh chart caches after restore', error)
      }
      if (parsePatientChartSplat(params['*']).resourceId === resourceId) {
        setOpenRecordVersion((version) => version + 1)
      }

      showNotification({ color: 'green', message: 'Restored to chart.' })
    } catch (error) {
      logError('patient', 'Restore chart record', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to restore this item.',
      })
    } finally {
      setRestoringId(null)
    }
  }

  const handleConfirmRemove = async ({
    reason,
    explanation,
  }: {
    reason: ChartRecordRemovalReason
    explanation?: string
  }) => {
    if (!removingResource?.id) {
      return
    }
    const resourceType = removingResource.resourceType as ChartRecordResourceType
    const resourceId = removingResource.id
    const isOpenRecord = parsePatientChartSplat(params['*']).resourceId === resourceId
    setIsRemoving(true)
    try {
      const result = await client.chartRecordRemove({
        resourceType,
        resourceId,
        reason,
        explanation,
      })

      const updatedResources = canSeeRemoved
        ? resources.map((resource) =>
            resource.id === resourceId
              ? ({ ...resource, status: result.status } as AvailResource)
              : resource,
          )
        : resources.filter((resource) => resource.id !== resourceId)
      setResources(updatedResources)

      if (isOpenRecord && !canSeeRemoved) {
        void navigate(buildPatientChartRelativePath(item.dataKey, updatedResources[0]?.id))
      }

      try {
        medplum.invalidateSearches(resourceType)
        if (patient.id) {
          await queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patient.id) })
        }
      } catch (error) {
        logError('patient', 'Refresh chart caches after removal', error)
      }
      // Not after a failed removal: remounting would close an open editor on unsaved changes.
      if (isOpenRecord) setOpenRecordVersion((version) => version + 1)

      showNotification({ color: 'green', message: 'Removed from chart.' })
    } catch (error) {
      logError('patient', 'Remove chart record', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to remove this item.',
      })
      // The record may have changed while the dialog was open (a draft note locked, say), so
      // show its current state rather than the one the removal was refused for.
      try {
        const current = await medplum.readResource(resourceType, resourceId, { cache: 'no-cache' })
        setResources((prev) =>
          prev.map((resource) =>
            resource.id === resourceId ? (current as AvailResource) : resource,
          ),
        )
      } catch (refreshError) {
        logError('patient', 'Refresh chart record after failed removal', refreshError)
      }
    } finally {
      setIsRemoving(false)
    }
  }

  const resourceId = parsePatientChartSplat(params['*']).resourceId
  const firstResourceId = resources[0]?.id

  // Only auto-select when the resource has an id — navigating to section alone when
  // already on that section would re-render forever if id were missing.
  if (!resourceId && firstResourceId) {
    return <Navigate to={buildPatientChartRelativePath(item.dataKey, firstResourceId)} />
  }

  return (
    <div className="flex w-full items-start gap-2">
      <div
        id="scrollableDiv"
        className="fixed w-[280px] overflow-auto bg-[#FBFCFE] pb-[60px]"
        style={{ top: stickyTop, height: `calc(100vh - ${stickyTop})` }}
      >
        <div className="sticky top-0 z-10 w-full bg-[#FBFCFE] p-5">
          {action}
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            leftSection={<IconSearch width={16} />}
            rightSection={
              searchQuery ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents={searchQuery ? 'all' : 'none'}
          />
          {combinedPdf && (
            <CombinedPdfSelectionBar
              patient={patient}
              resourceType={combinedPdf.resourceType}
              fileLabel={combinedPdf.fileLabel}
              includableIds={combinableIds}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
            />
          )}
        </div>

        {!resources.length && (
          <div className="px-4 text-center text-sm">
            {isLoading ? 'Loading records...' : 'No records available'}
          </div>
        )}

        <InfiniteScroll
          dataLength={resources.length}
          next={handleLoadMore}
          hasMore={hasMore}
          loader={<Loader color="blue" />}
          scrollableTarget="scrollableDiv"
          style={{ display: 'contents' }}
        >
          {resources.map((resource, idx) => {
            // Every Provenance describing this resource, merged into one — the
            // list title reads the `source` agent, which often lives on a
            // sibling of whichever Provenance came back first (PD-1491).
            const resourceReference = `${resource.resourceType}/${resource.id}`
            const provenance = mergeProvenanceAgents(
              provenances.filter((item) =>
                item.target.some((t) => t.reference === resourceReference),
              ),
              resourceReference,
            )
            const displayData = item.getDisplayData(resource, provenance)

            let breakAll = false

            if (displayData.title && typeof displayData.title === 'string') {
              breakAll = !displayData.title.includes(' ') && displayData.title.length > 22
            }

            const isRemoved = isRemovedResource(resource)

            if (displayData.title)
              return (
                <div
                  key={resource.id ?? idx}
                  onClick={() => navigate(buildPatientChartRelativePath(item.dataKey, resource.id))}
                  className={[
                    'cursor-pointer border-b border-b-[#D0E1F2] px-4 py-2 text-[13px]',
                    isRemoved ? 'opacity-60' : '',
                    resource.id === resourceId ? 'bg-[#dcebfd]' : 'hover:bg-[#F0F7FF]',
                  ].join(' ')}
                >
                  <div className="flex justify-between">
                    {combinedPdf && !isRemoved && resource.id && (
                      <RecordSelectCheckbox
                        id={resource.id}
                        title={typeof displayData.title === 'string' ? displayData.title : 'record'}
                        includable={isCombinableRecord(resource)}
                        selectedIds={selectedIds}
                        onChange={setSelectedIds}
                      />
                    )}
                    <p
                      className={`text-primary flex-1 wrap-break-word ${breakAll ? 'break-all' : ''}`}
                    >
                      {displayData.title}
                      {isRemoved && (
                        <span
                          data-testid="chart-record-removed-pill"
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            fontSize: 9.5,
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: 999,
                            background: 'rgba(229, 83, 61, 0.12)',
                            color: '#E5533D',
                          }}
                        >
                          Removed
                        </span>
                      )}
                      {displayData.aiBacked && (
                        // LD-21 — mockup parity with
                        // `ui-designs/.../ChartPage.tsx:1622`. Indigo pill
                        // surfaces scribe-backed Progress Notes in the left
                        // rail so doctors can spot AI-authored notes at a
                        // glance. Inline `style` matches the exact tokens
                        // from the mockup (rgba(99,102,241,0.12) /
                        // #6366f1 / 9.5px / 700 / 1px 5px / 999px) — these
                        // aren't yet brand tokens so we keep them literal.
                        <span
                          data-testid="progress-note-ai-pill"
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            fontSize: 9.5,
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: 999,
                            background: 'rgba(99, 102, 241, 0.12)',
                            color: '#6366f1',
                          }}
                        >
                          AI
                        </span>
                      )}
                      {displayData.draftBacked && (
                        // Surfaces preliminary (unlocked) Progress Notes in
                        // the left rail. Amber treatment communicates
                        // "needs locking" without alarming the doctor.
                        <span
                          data-testid="progress-note-draft-pill"
                          style={{
                            display: 'inline-block',
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            fontSize: 9.5,
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: 999,
                            background: 'rgba(217, 119, 6, 0.12)',
                            color: '#b45309',
                          }}
                        >
                          Draft
                        </span>
                      )}
                    </p>
                    <div className="flex items-start gap-1">
                      <p className="text-[#7D7987]">{displayData.date}</p>
                      {(canRemoveFromChart(resource) ||
                        canRestoreFromChart(resource) ||
                        !!removalBlock(resource)) && (
                        <Menu shadow="md" position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              aria-label="More actions"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <IconDotsVertical size={14} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                            <Menu.Item
                              leftSection={<IconHistory size={14} />}
                              onClick={() => setActivityResource(resource)}
                            >
                              View activity
                            </Menu.Item>
                            {canRemoveFromChart(resource) && (
                              <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => setRemovingResource(resource)}
                              >
                                Remove from chart
                              </Menu.Item>
                            )}
                            {removalBlock(resource) && (
                              <Menu.Item leftSection={<IconTrash size={14} />} disabled>
                                Remove from chart
                                <span className="block text-xs">{removalBlock(resource)}</span>
                              </Menu.Item>
                            )}
                            {canRestoreFromChart(resource) && (
                              <Menu.Item
                                color="green"
                                leftSection={<IconRestore size={14} />}
                                disabled={restoringId === resource.id}
                                onClick={() => void handleRestore(resource)}
                              >
                                Restore to chart
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </div>
                  </div>
                  <p className="text-[#7D7987]">{displayData.description}</p>
                </div>
              )
          })}
        </InfiniteScroll>
      </div>

      <div
        key={openRecordVersion}
        className="ml-[280px] flex-1"
        style={{ width: 'calc(100% - 280px)' }}
      >
        <NoteEditorContext.Provider value={setNoteInEditor}>{children}</NoteEditorContext.Provider>
      </div>

      <TypedDeleteConfirmModal
        opened={!!removingResource}
        onClose={() => setRemovingResource(null)}
        onConfirm={handleConfirmRemove}
        loading={isRemoving}
        recordTypeLabel={item.title}
        createdOnLabel={removingResource ? item.getDisplayData(removingResource).date : undefined}
      />
      <ChartRecordActivityModal
        opened={!!activityResource}
        onClose={() => setActivityResource(null)}
        resourceType={
          (activityResource?.resourceType as ChartRecordResourceType) ?? 'DocumentReference'
        }
        resourceId={activityResource?.id}
      />
    </div>
  )
}
